/**
 * PDF Image Utilities
 *
 * Provides image compression helpers to prevent the pdfmake
 * "Cannot create final Buffer. It would be larger than 0x7fffffff bytes" error.
 *
 * Strategy: Convert any image to JPEG at reduced quality + constrained dimensions
 * before embedding into a pdfmake docDefinition. This dramatically reduces the
 * base64 payload size without affecting visual quality at typical PDF print sizes.
 */

/** Maximum dimension (width or height) for any image embedded in a PDF. */
const MAX_IMAGE_DIMENSION = 800;

/** JPEG quality (0–1) for logo / general images. */
const LOGO_JPEG_QUALITY = 0.75;

/** JPEG quality (0–1) for QR codes (keep higher for readability). */
const QR_JPEG_QUALITY = 0.92;

/** JPEG quality (0–1) for signature images. */
const SIGNATURE_JPEG_QUALITY = 0.80;

/**
 * Compress a base64 image string to a smaller JPEG.
 *
 * @param base64 - Original base64 data URL (data:image/...;base64,...)
 *                 OR a plain URL string (will be returned as-is, not compressed).
 * @param quality - JPEG quality 0–1 (default 0.75)
 * @param maxDimension - Max width/height in pixels (default 800)
 * @returns Promise resolving to a compressed base64 JPEG data URL,
 *          or the original string if compression is not possible / not needed.
 */
export async function compressPdfImage(
  base64: string,
  quality: number = LOGO_JPEG_QUALITY,
  maxDimension: number = MAX_IMAGE_DIMENSION,
): Promise<string> {
  if (!base64 || typeof base64 !== 'string') return base64;

  // Only compress data URLs — plain http/https URLs are returned unchanged
  if (!base64.startsWith('data:')) return base64;

  // Already tiny (< 20 KB raw bytes) — no compression needed
  const rawByteEstimate = (base64.length * 3) / 4;
  if (rawByteEstimate < 20_000) return base64;

  return new Promise<string>((resolve) => {
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      // Scale down if either dimension exceeds the limit
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        // Canvas not available — return original
        resolve(base64);
        return;
      }

      // White background so transparent PNGs don't become black in JPEG
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };

    img.onerror = () => {
      // If loading fails, return original unchanged
      resolve(base64);
    };

    img.src = base64;
  });
}

/**
 * Compress a logo image for PDF embedding.
 * Uses a moderate quality suitable for business logos.
 */
export function compressLogoForPdf(base64: string): Promise<string> {
  return compressPdfImage(base64, LOGO_JPEG_QUALITY, MAX_IMAGE_DIMENSION);
}

/**
 * Compress a QR code image for PDF embedding.
 * Uses higher quality to keep QR codes scannable.
 */
export function compressQrCodeForPdf(base64: string): Promise<string> {
  return compressPdfImage(base64, QR_JPEG_QUALITY, 300);
}

/**
 * Compress a signature image for PDF embedding.
 */
export function compressSignatureForPdf(base64: string): Promise<string> {
  return compressPdfImage(base64, SIGNATURE_JPEG_QUALITY, 400);
}

/**
 * Log the estimated size of a base64 image in kilobytes (for debugging).
 */
export function logImageSize(label: string, base64: string): void {
  if (!base64) return;
  const kb = Math.round((base64.length * 3) / 4 / 1024);
  console.log(`[PDF Image] ${label}: ~${kb} KB`);
}
